/* global React, ReactDOM, Icon, fmt, t */
// ============================================================
// PLUMME — Søk i alt (kommandopalett)
// Sentrert ⌘K-palett: ett søkefelt øverst, grupperte treff under,
// helt tastaturstyrt. Åpnes fra «Søk i alt» i topplinjen.
// Searches shortcuts, help articles, and active accounting data from appState.
// Results open through the owning page's existing navigation contract.
// ============================================================
const { useState: ssxUseState, useEffect: ssxUseEffect, useMemo: ssxUseMemo, useRef: ssxUseRef } = React;

// Help hits come straight from helpArticles() (help-articles.jsx) so a new article
// is searchable without a second list to keep in sync. Title matches rank first.
const ssxHelpHits = (m) => {
  const all = Object.values(window.helpArticles ? window.helpArticles() : {});
  const byTitle = all.filter((a) => m(a.title));
  const byText = all.filter((a) => !byTitle.includes(a) && (m(a.keywords) || m(a.lead) || m(a.category)));
  return byTitle.concat(byText);
};

// Quick-pick labels are display fallbacks only; they are resolved at render through
// navLabel() from the `nav` route id. Never call t() in module-level constants.
const SSX_QUICK = [
  { label: 'Nytt salg', icon: 'fileText', nav: 'faktura' },
  { label: 'Nytt kjøp', icon: 'bag', nav: 'nyttkjop' },
  { label: 'Kunder', icon: 'users', nav: 'kunder' },
  { label: 'Innboks', icon: 'mail', nav: 'innboks' },
  { label: 'MVA-melding', icon: 'percent', nav: 'innsendinger' },
  { label: 'Lønn', icon: 'wallet', nav: 'lonn' },
];

const SSX_RECENT_KEY = 'plumme-supersearch-recent';
// Seed chips follow the active locale at call time; persisted searches are never rewritten.
const ssxDefaultRecent = () => t('srch.recentDefaults', 'faktura|lønn|MVA').split('|');
const ssxLoadRecent = () => { try { const r = JSON.parse(localStorage.getItem(SSX_RECENT_KEY) || 'null'); return Array.isArray(r) && r.length ? r : ssxDefaultRecent(); } catch (e) { return ssxDefaultRecent(); } };
const ssxNavLabel = (item) => (window.navLabel ? window.navLabel(item) : item.label);
const ssxSaveRecent = (q) => { try { const cur = ssxLoadRecent().filter((x) => x.toLowerCase() !== q.toLowerCase()); const next = [q, ...cur].slice(0, 6); localStorage.setItem(SSX_RECENT_KEY, JSON.stringify(next)); return next; } catch (e) { return ssxLoadRecent(); } };

function ssxBuild(query, state) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const m = (s) => String(s || '').toLowerCase().includes(q);
  const groups = [];

  const navItems = (window.PLUMME_NAV || []).flatMap((sec) => sec.items || []);
  const snar = navItems.filter((it) => m(ssxNavLabel(it)) || m(it.label)).slice(0, 6)
    .map((it) => ({ id: 'nav-' + it.key, icon: it.icon || 'arrowRight', title: ssxNavLabel(it), sub: t('srch.nav.sub'), tag: t('set.page'), nav: it.key }));
  if (snar.length) groups.push({ key: 'snarvei', label: t('srch.group.shortcuts'), items: snar, total: snar.length });

  const businessGroups = window.PlummeGlobalSearch?.search?.(state || {}, query, { limitPerGroup: 6 }) || [];
  businessGroups.forEach((group) => groups.push({
    key: group.key,
    label: t(`srch.group.${group.key}`, group.label),
    total: group.total,
    items: group.items.map((item) => ({
      ...item,
      sub: item.subtitle,
      tag: t(`srch.tag.${String(item.id || '').split(':')[0]}`, item.tag),
      meta: item.amount == null ? item.meta : fmt(item.amount),
    })),
  }));

  const help = ssxHelpHits(m);
  if (help.length) groups.push({ key: 'hjelp', label: t('rep.help'), total: help.length,
    items: help.slice(0, 8).map((h) => ({ id: 'help-' + h.id, icon: 'help', title: h.title, sub: t('srch.help.sub'), tag: t('rep.help'), articleId: h.id, nav: h.route })) });

  return groups;
}

function SsxRow({ it, active, onHover, onClick }) {
  return <button className={'ssx-row' + (active ? ' active' : '')} onMouseMove={onHover} onClick={onClick}>
    <span className="ssx-row-ico"><Icon name={it.icon} size={18} /></span>
    <span className="ssx-row-body">
      <span className="ssx-row-title">{it.dot && <span className="ssx-dot" style={{ background: it.dot }} />}{it.title}</span>
      {it.sub && <span className="ssx-row-sub">{it.sub}</span>}
    </span>
    {it.meta && <span className="ssx-row-meta">{it.meta}</span>}
    <span className="ssx-row-tag">{it.tag}</span>
    <span className="ssx-row-enter"><Icon name="arrowRight" size={15} /></span>
  </button>;
}

function SuperSearch({ onClose, initialQuery }) {
  const backend = window.PlummeBackend?.useBackend ? window.PlummeBackend.useBackend() : null;
  const [q, setQ] = ssxUseState(initialQuery || '');
  const [recent, setRecent] = ssxUseState(ssxLoadRecent);
  const [idx, setIdx] = ssxUseState(0);
  const inputRef = ssxUseRef(null);
  const listRef = ssxUseRef(null);

  const state = backend?.appState || window.PlummeBackend?.appState || {};
  const groups = ssxUseMemo(() => ssxBuild(q, state), [q, state]);
  const flat = ssxUseMemo(() => groups.flatMap((g) => g.items), [groups]);
  const sel = flat[Math.min(idx, Math.max(0, flat.length - 1))] || null;

  ssxUseEffect(() => { setIdx(0); }, [q]);
  ssxUseEffect(() => { const prev = document.body.style.overflow; document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = prev; }; }, []);
  ssxUseEffect(() => { const timer = setTimeout(() => inputRef.current && inputRef.current.focus(), 30); return () => clearTimeout(timer); }, []);

  // keep active row in view (without scrollIntoView)
  ssxUseEffect(() => {
    const c = listRef.current; if (!c) return;
    const el = c.querySelector('.ssx-row.active'); if (!el) return;
    const cr = c.getBoundingClientRect(), er = el.getBoundingClientRect();
    if (er.top < cr.top) c.scrollTop -= (cr.top - er.top) + 10;
    else if (er.bottom > cr.bottom) c.scrollTop += (er.bottom - cr.bottom) + 10;
  }, [idx, q]);

  const go = (item) => {
    const target = item || sel; if (!target) return;
    if (q.trim()) { setRecent(ssxSaveRecent(q.trim())); window.__lastSearchQuery = q.trim(); }
    if (target.articleId && window.openHelpArticle) { onClose(); window.openHelpArticle(target.articleId); return; }
    if (target.target && window.PlummeGlobalSearch?.open) { onClose(); window.PlummeGlobalSearch.open(target); return; }
    if (target.nav && window.plummeNav) window.plummeNav(target.nav);
    onClose();
  };

  ssxUseEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (!flat.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(flat.length - 1, i + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
      else if (e.key === 'Enter') { e.preventDefault(); go(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flat, sel, q]);

  const magnifier = (window.__resources && window.__resources.plommeMagnifier) || 'src/img/plommeMagnifier.png';
  let running = -1;
  // t() returns a string, so a sentinel marks where the bold query goes; each locale owns its quote characters.
  const noMatchParts = t('srch.noMatches', { query: '\u0000' }).split('\u0000');

  // Paletten monteres i .topbar, som er position:sticky med z-index og dermed lager
  // en egen stacking context. Uten portal blir z-index 1200/1201 låst inne der, og
  // bunnmenyen (z-index 75 i rot-konteksten) legger seg oppå søket på mobil.
  const content = <React.Fragment>
    <div className="ssx-scrim" onClick={onClose} />
    <div className="ssx-palette" role="dialog" aria-label={t('shell.searchAll')}>
      <div className="ssx-pal-search">
        <img src={magnifier} alt="" className="ssx-pal-mascot" />
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('srch.placeholder')} />
        {q ? <button className="ssx-pal-clear" onClick={() => { setQ(''); inputRef.current && inputRef.current.focus(); }} aria-label={t('po.clearSearch')}><Icon name="x" size={16} /></button>
          : <button className="ssx-pal-esc" onClick={onClose}>{t('srch.esc')}</button>}
      </div>

      <div className="ssx-pal-body" ref={listRef}>
        {!q && <React.Fragment>
          <div className="ssx-block">
            <div className="ssx-block-label">{t('srch.recent')}</div>
            <div className="ssx-chips">{recent.map((r) => <button key={r} className="ssx-chip" onClick={() => setQ(r)}><Icon name="search" size={13} />{r}</button>)}</div>
          </div>
          <div className="ssx-block">
            <div className="ssx-block-label">{t('set.quickPick')}</div>
            <div className="ssx-quick-grid">
              {SSX_QUICK.map((a) => <button key={a.nav} className="ssx-quick" onClick={() => { if (window.plummeNav) window.plummeNav(a.nav); onClose(); }}>
                <span className="ssx-quick-ico"><Icon name={a.icon} size={18} /></span>{ssxNavLabel({ key: a.nav, label: a.label })}
              </button>)}
            </div>
          </div>
        </React.Fragment>}

        {q && !flat.length && <div className="ssx-noresult">
          <img src={magnifier} alt="" />
          <p>{noMatchParts[0]}<b>{q}</b>{noMatchParts[1]}<br />{t('srch.tryPageName')}</p>
        </div>}

        {q && groups.map((g) => <div key={g.key} className="ssx-pal-group">
          <div className="ssx-pal-group-head">{g.label}<span>{g.total}</span></div>
          {g.items.map((it) => { running += 1; const myIdx = running; return <SsxRow key={it.id} it={it} active={sel && sel.id === it.id} onHover={() => setIdx(myIdx)} onClick={() => go(it)} />; })}
        </div>)}
      </div>

      <div className="ssx-pal-foot">
        <span className="ssx-hints"><span><kbd>↑</kbd><kbd>↓</kbd> {t('srch.hint.browse')}</span><span><kbd>↵</kbd> {t('srch.hint.open')}</span><span><kbd>{t('srch.esc')}</kbd> {t('srch.hint.close')}</span></span>
        {q && flat.length > 0 && <span className="ssx-count">{t('srch.countShown', { count: flat.length })}</span>}
      </div>
    </div>
  </React.Fragment>;
  return ReactDOM?.createPortal ? ReactDOM.createPortal(content, document.body) : content;
}

window.openSearch = (query) => window.dispatchEvent(new CustomEvent('plumme-open-search', { detail: query || '' }));
Object.assign(window, { SuperSearch });
